import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { API_BASE } from "@/services/api";
import {
  ArrowLeft, Trophy, Target, TrendingUp, BarChart3,
  Crown, Medal, Flame, Star, Users, Zap, PenTool,
  ChevronUp, ChevronDown, Loader2, Award, Brain,
  CheckCircle, AlertTriangle, BookOpen
} from "lucide-react";

const NationalBenchmarkPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [myRank, setMyRank] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [lbMetric, setLbMetric] = useState("overall");
  const [lbLoading, setLbLoading] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { loadMyRank(); }, []);

  const loadMyRank = async () => {
    try {
      const res = await fetch(`${API_BASE}/aspirant-network/benchmark/my-rank`, { headers });
      setMyRank(await res.json());
    } catch { toast({ variant: "destructive", description: "Failed to load ranking" }); }
    finally { setLoading(false); }
  };

  const loadLeaderboard = async (metric) => {
    setLbMetric(metric);
    setLbLoading(true);
    try {
      const res = await fetch(`${API_BASE}/aspirant-network/benchmark/leaderboard?metric=${metric}`, { headers });
      const data = await res.json();
      setLeaderboard(data.leaders || []);
    } catch {}
    finally { setLbLoading(false); }
  };

  useEffect(() => { loadLeaderboard(lbMetric); }, []);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-indigo-500" /></div>;
  }

  const o = myRank?.overall || {};
  const percentileColor = o.percentile >= 90 ? "text-emerald-600" : o.percentile >= 70 ? "text-blue-600" : o.percentile >= 50 ? "text-amber-600" : "text-red-600";
  const percentileBg = o.percentile >= 90 ? "from-emerald-500 to-teal-500" : o.percentile >= 70 ? "from-blue-500 to-indigo-500" : o.percentile >= 50 ? "from-amber-500 to-orange-500" : "from-red-500 to-rose-500";

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30">
      {/* Header */}
      <div className={`bg-gradient-to-r ${percentileBg} text-white`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate("/dashboard")} className="p-2 bg-white/10 hover:bg-white/20 rounded-xl" data-testid="benchmark-back-btn">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2"><Trophy className="w-6 h-6" /> National Benchmark</h1>
                <p className="text-white/70 text-sm">Your position among all CLARIAS aspirants</p>
              </div>
            </div>
            <Badge className="bg-white/20 text-white border-0 text-sm px-3 py-1">
              <Users className="w-4 h-4 mr-1" /> {myRank?.total_users?.toLocaleString() || 0} users
            </Badge>
          </div>

          {/* Main Rank Card */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center" data-testid="main-rank-card">
            <p className="text-white/60 text-xs uppercase tracking-wider mb-2">Your National Rank</p>
            <div className="flex items-center justify-center gap-3">
              <Crown className="w-8 h-8 text-amber-300" />
              <p className="text-5xl font-bold">#{o.rank?.toLocaleString() || '—'}</p>
            </div>
            <p className="text-white/70 text-sm mt-1">out of {o.total?.toLocaleString()} aspirants</p>
            <div className="mt-4 inline-flex items-center gap-2 bg-white/20 rounded-full px-5 py-2">
              <Zap className="w-4 h-4" />
              <span className="font-bold text-lg">Top {(100 - (o.percentile || 0)).toFixed(1)}%</span>
              <span className="text-white/60 text-sm">· {o.percentile?.toFixed(1)}th percentile</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Performance Breakdown */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-500 to-indigo-600 text-white">
            <CardContent className="p-4 text-center">
              <Target className="w-6 h-6 mx-auto mb-1 opacity-80" />
              <p className="text-2xl font-bold">{myRank?.mcq?.accuracy || 0}%</p>
              <p className="text-xs text-blue-200">MCQ Accuracy</p>
              <p className="text-[10px] text-blue-300 mt-1">Rank: #{myRank?.mcq?.rank?.toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-lg bg-gradient-to-br from-purple-500 to-pink-600 text-white">
            <CardContent className="p-4 text-center">
              <PenTool className="w-6 h-6 mx-auto mb-1 opacity-80" />
              <p className="text-2xl font-bold">{myRank?.answer_writing?.avg_score || 0}</p>
              <p className="text-xs text-purple-200">Avg Answer Score</p>
              <p className="text-[10px] text-purple-300 mt-1">{myRank?.answer_writing?.submissions || 0} submissions</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-lg bg-gradient-to-br from-emerald-500 to-teal-600 text-white">
            <CardContent className="p-4 text-center">
              <BarChart3 className="w-6 h-6 mx-auto mb-1 opacity-80" />
              <p className="text-2xl font-bold">{myRank?.tests?.avg_percentage || 0}%</p>
              <p className="text-xs text-emerald-200">Test Avg</p>
              <p className="text-[10px] text-emerald-300 mt-1">{myRank?.tests?.taken || 0} tests taken</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-lg bg-gradient-to-br from-amber-500 to-orange-600 text-white">
            <CardContent className="p-4 text-center">
              <Flame className="w-6 h-6 mx-auto mb-1 opacity-80" />
              <p className="text-2xl font-bold">{myRank?.consistency?.current_streak || 0}</p>
              <p className="text-xs text-amber-200">Day Streak</p>
              <p className="text-[10px] text-amber-300 mt-1">{myRank?.consistency?.active_days_30 || 0} active days/30</p>
            </CardContent>
          </Card>
        </div>

        {/* Strengths & Weaknesses */}
        <div className="grid md:grid-cols-2 gap-4">
          <Card className="border-emerald-200">
            <CardContent className="p-5">
              <h3 className="font-bold text-emerald-700 mb-3 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" /> Strong Subjects
              </h3>
              {myRank?.strengths?.length > 0 ? (
                <div className="space-y-2">
                  {myRank.strengths.map((s, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 bg-emerald-50 rounded-lg">
                      <Star className="w-4 h-4 text-emerald-500" />
                      <span className="text-sm font-medium text-slate-800 capitalize">{s.replace(/_/g, ' ')}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500">Practice more MCQs to identify strengths</p>
              )}
            </CardContent>
          </Card>
          <Card className="border-red-200">
            <CardContent className="p-5">
              <h3 className="font-bold text-red-700 mb-3 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> Weak Subjects
              </h3>
              {myRank?.weaknesses?.length > 0 ? (
                <div className="space-y-2">
                  {myRank.weaknesses.map((s, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-red-50 rounded-lg">
                      <span className="text-sm font-medium text-slate-800 capitalize">{s.replace(/_/g, ' ')}</span>
                      <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => navigate("/prelims/chapter-practice")}>
                        Practice
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500">No weak areas detected yet — keep practicing!</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Subject-wise Performance */}
        {myRank?.subject_ranks?.length > 0 && (
          <Card>
            <CardContent className="p-5">
              <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-indigo-500" /> Subject-wise Accuracy
              </h3>
              <div className="space-y-3">
                {myRank.subject_ranks.map((s) => (
                  <div key={s.subject} className="flex items-center gap-3">
                    <span className="text-sm text-slate-700 w-32 capitalize truncate">{s.subject.replace(/_/g, ' ')}</span>
                    <div className="flex-1">
                      <Progress value={s.accuracy} className="h-2" />
                    </div>
                    <span className={`text-sm font-bold w-14 text-right ${s.accuracy >= 70 ? 'text-emerald-600' : s.accuracy >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                      {s.accuracy}%
                    </span>
                    <span className="text-xs text-slate-400 w-12 text-right">{s.attempted}Q</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* National Leaderboard */}
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-slate-900 flex items-center gap-2">
                <Trophy className="w-5 h-5 text-amber-500" /> National Leaderboard
              </h3>
              <div className="flex gap-1">
                {[
                  { key: "overall", label: "Overall" },
                  { key: "answer_writing", label: "Answers" },
                  { key: "mcq", label: "MCQ" },
                ].map(m => (
                  <button key={m.key} onClick={() => loadLeaderboard(m.key)}
                    className={`px-3 py-1 rounded-full text-xs font-medium ${lbMetric === m.key ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600'}`}
                    data-testid={`lb-${m.key}`}>
                    {m.label}
                  </button>
                ))}
              </div>
            </div>

            {lbLoading ? (
              <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin text-indigo-500 mx-auto" /></div>
            ) : leaderboard.length === 0 ? (
              <p className="text-center text-slate-500 py-8">No data yet. Start practicing to appear here!</p>
            ) : (
              <div className="space-y-2">
                {leaderboard.slice(0, 20).map((l, idx) => (
                  <div key={l.user_id || idx}
                    className={`flex items-center justify-between p-3 rounded-xl ${l.is_you ? 'bg-indigo-100 border-2 border-indigo-300' : idx < 3 ? 'bg-amber-50' : 'bg-slate-50'}`}
                    data-testid={`lb-row-${idx}`}>
                    <div className="flex items-center gap-3">
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        idx === 0 ? 'bg-amber-500 text-white' : idx === 1 ? 'bg-slate-300 text-slate-700' : idx === 2 ? 'bg-orange-400 text-white' : 'bg-white text-slate-600 border'
                      }`}>
                        {l.rank || idx + 1}
                      </span>
                      <div>
                        <p className={`font-medium text-sm ${l.is_you ? 'text-indigo-700' : 'text-slate-900'}`}>
                          {l.name || 'Aspirant'} {l.is_you && <span className="text-xs text-indigo-500">(You)</span>}
                        </p>
                        <p className="text-xs text-slate-500">
                          {lbMetric === "mcq" ? `${l.attempted} MCQs · ${l.accuracy}% acc` :
                           lbMetric === "answer_writing" ? `${l.submissions} answers · ${l.upvotes} votes` :
                           `${l.submissions || 0} submissions`}
                        </p>
                      </div>
                    </div>
                    <p className="font-bold text-lg text-slate-800">
                      {lbMetric === "mcq" ? `${l.accuracy}%` : `${l.score || l.avg_score || 0}`}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* CTA */}
        <div className="grid grid-cols-2 gap-3">
          <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={() => navigate("/answer-arena")} data-testid="go-arena-btn">
            <PenTool className="w-4 h-4 mr-2" /> Daily Answer Arena
          </Button>
          <Button variant="outline" onClick={() => navigate("/prelims/chapter-practice")} data-testid="go-mcq-btn">
            <Target className="w-4 h-4 mr-2" /> Practice MCQs
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NationalBenchmarkPage;
